import { call, put, takeLatest, all, select } from "redux-saga/effects";
import {
	startLoginRequest,
	startLoginSuccess,
	startLoginFailure,
	verifyOtpRequest,
	verifyOtpSuccess,
	verifyOtpFailure,
	refreshTokenCall,
} from "../slices/auth.slice";
import { type PayloadAction } from "@reduxjs/toolkit";
import {
	type AuthStartRequest,
	type StartLoginPayload,
	type VerifyOtpPayload,
} from "../types/auth.types";
import { refreshToken, sendOTP, verifyOtp } from "../../services/api.services";
import { logClientError } from "../../services/client-logger.service";

import type { AxiosResponse } from "axios";
import { setOtpModalVisible } from "../slices/common.slice";
import { orderQuoteRequested } from "../slices/order.slice";
import { RootState, store } from "../root";
import { initiateUpload } from "../slices/upload.slice";
import { OrderType, QuoteOrderPayload } from "../types/order.types";
import { resetform, setOtpVerified } from "../slices/uploadform.slice";
import { toast } from "sonner";
import { uploadFormSchema } from "@/config/uploadform.schema";

/**
 * STEP 1: Start + Send Token
 */
function* handleStartLogin(action: PayloadAction<StartLoginPayload>) {
	try {
		// /auth/start

		const { RecaptchaToken } = action.payload;
		const body: AuthStartRequest = {
			email: action.payload.email,
		};

		const startLoginResponse: AxiosResponse = yield call(
			sendOTP,
			body,
			RecaptchaToken
		);

		yield put(
			startLoginSuccess({ sessionId: startLoginResponse.data.data.code })
		);
		yield put(setOtpModalVisible(true));
	} catch (error: any) {
		yield call(logClientError, {
			level: "error",
			event: "start_login_failed",
			message: "Start login API failed",
			error,
			context: {
				email: action.payload.email,
				statusCode: error?.response?.status,
			},
		});
		yield put(
			startLoginFailure(error?.response?.data?.message ?? "Login failed")
		);
	}
}

/**
 * STEP 2: Verify OTP
 */
function* handleVerifyOtp(
	action: PayloadAction<VerifyOtpPayload>
): Generator<any, void, any> {
	const { pwuserid, locationAddressId, applicationType } = yield select(
		(state: RootState) => state.location.location
	);

	const {
		description,
		email,
		phone,
		purpose,
		dueDate,
		jobReference,
		company,
		fullName,
		sizeprint,
		papertype,
		binding,
		noofSets,
		arcaccountno,
		address1,
		city,
		CountryName,
		ZipCode,
		StateName,
		willcall,
		countryid,
		stateid,
	} = yield select((state: RootState) => state.uploadForm);
	const { product } = yield select((state: RootState) => state.common);

	const formValidation = uploadFormSchema.safeParse({
		email,
		phone,
		fullName,
		company,
		jobReference,
		dueDate,
		purpose,
		description,
		noofSets,
		sizeprint,
		ZipCode,
		arcaccountno,
		address1,
		willcall,
	});

	if (!formValidation.success) {
		toast.error("Please correct the highlighted fields before continuing.");
		yield put(verifyOtpFailure("Invalid form data"));
		return;
	}

	try {
		const body: AuthStartRequest = {
			email: action.payload.email,
		};
		const response: AxiosResponse = yield call(verifyOtp, {
			...body,
			otp: action.payload.otp,
		});

		yield put(
			verifyOtpSuccess({
				token: response?.data?.data?.token?.access_token,
				refreshToken: response?.data?.data?.token?.refresh_token,
			})
		);

		yield put(
			setOtpVerified({
				email: action.payload.email,
				timestamp: new Date().toISOString(),
			})
		);

		const data: QuoteOrderPayload = {
			firstname: fullName.split(" ")[0],
			lastname: fullName.split(" ").slice(1).join(" "),
			pwaccountid: pwuserid,
			orderinstruction: description,
			email,
			phoneno: phone,
			// ordertype: OrderType.PRINT_ORDER,
			pono: phone,
			purpose,
			orderduedate: dueDate,
			jobname: jobReference,
			company,
			receiverpwaccountid: pwuserid,
			receiveraccountaddressid: locationAddressId,
			timezoneoffset: -new Date().getTimezoneOffset() / 60,
			sourceapptype: applicationType
				? applicationType
				: product === "arc"
					? 2
					: 1,
			printsize: sizeprint || "",
			papertype: papertype,
			bindtype: binding,
			noofsets: noofSets || "1",
			arcaccountno: arcaccountno || "",
			willcall: !!willcall,

			...(!willcall && {
				address1: address1 || "",
				city: city || "",
				CountryName: CountryName || "",
				ZipCode: ZipCode || "",
				StateName: StateName || "",
				stateid: stateid || "",
				countryid: countryid || "",
			}),
		};

		yield put(orderQuoteRequested(data));

		yield put(setOtpModalVisible(false));
	} catch (error: any) {
		yield call(logClientError, {
			level: "error",
			event: "verify_otp_failed",
			message: "OTP verification API failed",
			error,
			context: {
				email: action.payload.email,
				statusCode: error?.response?.status,
			},
		});
		yield put(
			verifyOtpFailure(
				error?.response?.data?.message ?? "OTP verification failed"
			)
		);
	}
}

function* handleGlobalUnauthorizedSaga(): any {
	try {
		const { refreshtoken } = yield select((state: RootState) => state.auth);

		// 1. Call the refresh token API using your Refresh Interceptor
		const res = yield call(refreshToken, {
			clientid: import.meta.env.VITE_CLIENT_ID,
			refresh_token: refreshtoken,
		});

		// 2. Set the new tokens
		yield put(
			verifyOtpSuccess({
				token: res?.data?.data.access_token,
				refreshToken: res?.data?.data.refresh_token, // Fixed: use the actual refresh token from response
			})
		);

		// Note: The next time an API is called, the interceptor will use the new token.
	} catch (error: any) {
		yield call(logClientError, {
			level: "error",
			event: "refresh_token_failed",
			message: "Refresh token flow failed",
			error,
			context: {
				statusCode: error?.response?.status,
			},
		});
		yield put(resetform());
		// Show the snackbar message
		toast.error("Session expired. Please login again to continue.");
	}
}

/**
 * Root Auth Saga
 */
export default function* authSaga() {
	yield all([
		takeLatest(startLoginRequest.type, handleStartLogin),
		takeLatest(verifyOtpRequest.type, handleVerifyOtp),
		takeLatest(refreshTokenCall.type, handleGlobalUnauthorizedSaga),
	]);
}
